import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router";

import { MapEditorObservationPanel } from "~/modules/mapEditor/components/MapEditorObservationPanel";
import { MapEditorObservationPanelEcologicalFunctionFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/components/EcologicalFunctionCard/fragments.generated";
import { MapEditorObservationPanelObservationMediaPointFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/components/ObservationMediaPointCard/fragments.generated";
import { MapEditorObservationPanelObservationFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/fragments.generated";

import { ProjectReviewVisitV2Screen_GetDataDocument } from "../../../../queries.generated";

export function ProjectReviewVisitV2ObservationDetailRoute() {
    const { clientSlug, projectSlug, visitId, mapEditorFeatureId } = useParams();

    const { t } = useTranslation("project");
    const navigate = useNavigate();
    const client = useApolloClient();

    if (!clientSlug || !projectSlug || !visitId || !mapEditorFeatureId) {
        throw new Error("Parameter `clientSlug`, `projectSlug`, `visitId`, and `mapEditorFeatureId` are required");
    }

    const createLinkToObservationMediaCard = (element: MapEditorObservationPanelObservationMediaPointFragment) => {
        return generatePath("/c/:clientSlug/p/:projectSlug/map-editor/:layerId/:featureId", {
            clientSlug,
            projectSlug,
            layerId: element.feature.layerId,
            featureId: element.mapEditorFeatureId,
        });
    };

    const handleAfterDelete = async () => {
        await client.refetchQueries({ include: [ProjectReviewVisitV2Screen_GetDataDocument] });
    };

    const handleObservationCloned = (clonedObservation: MapEditorObservationPanelObservationFragment) => {
        void client.refetchQueries({ include: [ProjectReviewVisitV2Screen_GetDataDocument] });
        navigate(
            generatePath("/c/:clientSlug/p/:projectSlug/review-visit/:visitId/observations/:mapEditorFeatureId", {
                clientSlug,
                projectSlug,
                visitId,
                mapEditorFeatureId: clonedObservation.feature.id,
            }),
        );
    };

    return (
        <MapEditorObservationPanel
            backHref=".."
            createHrefToEcologicalFunction={createLinkToEcologicalFunction}
            createHrefToObservationMediaPointCard={createLinkToObservationMediaCard}
            mapEditorFeatureId={mapEditorFeatureId}
            onEcologicalFunctionCreated={element => {
                navigate(`../../functions/created/${element.feature.id}`);
            }}
            onObservationCloned={handleObservationCloned}
            onObservationDeleted={handleAfterDelete}
            readonly={false}
            title={t("projectReviewVisitV2.observationsRoute.observationDetailTitle")}
        />
    );
}

function createLinkToEcologicalFunction(element: MapEditorObservationPanelEcologicalFunctionFragment) {
    return `../../functions/created/${element.featureId}`;
}
